import { ListChecks } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  /** 「检查规则」tab 已挂载的套件总数。 */
  checkpointMounted: number;
  /** 检查规则 verdict 聚合：通过 / 警告 / 失败 + 超时。 */
  pass: number;
  mid: number;
  fail: number;
  /** 「取值规则」tab 已挂载的 key 数量。 */
  kvKeys: number;
  /** 取值规则总命中次数（所有 watcher 的 count 之和）。 */
  kvHits: number;
  /** 面板当前是否打开（决定 active 视觉）。 */
  active: boolean;
  /** 工具栏空间是否充足；窄屏退化为图标 + 数字徽标。 */
  compact?: boolean;
  onClick: () => void;
}

/**
 * 「日志规则」工具栏统一入口。
 *
 * 设计动机：
 *   原本工具栏挂了两个独立按钮 `CheckpointEntryBadge`(检查点) 和
 *   `WatchEntryBadge`(日志摘要)。但它们打开的本质上是同一个右侧面板，
 *   只是定位到不同 tab；两个并列按钮既占工具栏空间，又让用户误以为
 *   是两个独立功能。统一为一个入口后：
 *     - 工具栏只占一格；
 *     - 视觉聚焦：fail / mid / hits 任一活跃都会染色，用户不必盯两个按钮；
 *     - 点击行为简化为 toggle 面板，tab 由内部 state 记忆。
 *
 * 染色优先级（dominant）：
 *     active(面板已开) > fail > mid > kvHits > pass > 灰底
 *   pass 排在 kvHits 后面：失败 / 警告显然更值得抢眼；有命中数 = 用户
 *   在乎的 key 出现了；纯通过则相对次要。
 *
 * Pip 列表：四个独立彩色圆点 + 数字（>0 才显示）：
 *   红 = fail，琥珀 = mid，绿 = pass，蓝绿 = kvHits。
 *   pass 和 kvHits 都用偏绿的色系但区分得开，避免视觉混淆。
 */
export function RuleEntryBadge({
  checkpointMounted,
  pass,
  mid,
  fail,
  kvKeys,
  kvHits,
  active,
  compact,
  onClick,
}: Props): JSX.Element {
  const hasMounted = checkpointMounted > 0 || kvKeys > 0;
  const hasAnyPip = fail > 0 || mid > 0 || pass > 0 || kvHits > 0;

  const dominant: 'fail' | 'mid' | 'hits' | 'pass' | 'idle' =
    fail > 0
      ? 'fail'
      : mid > 0
        ? 'mid'
        : kvHits > 0
          ? 'hits'
          : pass > 0
            ? 'pass'
            : 'idle';

  const baseCls = active
    ? 'border-accent bg-accent text-white shadow-sm hover:bg-accent/90'
    : dominant === 'fail'
      ? 'border-rose-500 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20'
      : dominant === 'mid'
        ? 'border-amber-500 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20'
        : dominant === 'hits'
          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
          : dominant === 'pass'
            ? 'border-emerald-500/60 bg-emerald-500/5 text-emerald-300/90 hover:bg-emerald-500/15'
            : 'border-line bg-bg-card text-ink hover:bg-bg-soft';

  // Tooltip：分两行讲清楚两侧状态。无挂载时给一个引导句。
  const titleParts: string[] = [];
  if (!hasMounted) {
    titleParts.push('打开日志规则面板（检查规则 + 取值规则）');
  } else {
    if (checkpointMounted > 0) {
      const verdictParts: string[] = [];
      if (fail > 0) verdictParts.push(`失败 ${fail}`);
      if (mid > 0) verdictParts.push(`警告 ${mid}`);
      if (pass > 0) verdictParts.push(`通过 ${pass}`);
      const verdictTail = verdictParts.length > 0 ? `（${verdictParts.join(' · ')}）` : '';
      titleParts.push(`检查规则 ${checkpointMounted} 套${verdictTail}`);
    }
    if (kvKeys > 0) {
      titleParts.push(`取值规则 ${kvKeys} key${kvHits > 0 ? ` · 命中 ${kvHits}` : ''}`);
    }
  }

  return (
    <button
      type="button"
      className={clsx(
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition',
        baseCls,
      )}
      onClick={onClick}
      title={titleParts.join('\n')}
    >
      <ListChecks className="h-3.5 w-3.5" />
      {compact ? (
        <span className="font-mono tabular-nums">
          {checkpointMounted + kvKeys || ''}
        </span>
      ) : (
        <>
          <span>日志规则</span>
          {hasAnyPip && (
            <span className="ml-0.5 inline-flex items-center gap-0.5 font-mono text-[10px] tabular-nums">
              {fail > 0 && <Pip color="bg-rose-400" label={fail} />}
              {mid > 0 && <Pip color="bg-amber-400" label={mid} />}
              {pass > 0 && <Pip color="bg-emerald-400" label={pass} />}
              {kvHits > 0 && <Pip color="bg-teal-400" label={kvHits} />}
            </span>
          )}
        </>
      )}
    </button>
  );
}

function Pip({ color, label }: { color: string; label: number }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-0.5">
      <span className={clsx('h-1.5 w-1.5 rounded-full', color)} />
      {label}
    </span>
  );
}
